import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { UploadSimple, FileArrowDown, Plus, PencilSimple, Trash, Envelope, X, MagnifyingGlass, Warning, PaperPlaneTilt } from "@phosphor-icons/react";

const ESTADOS = [
    { v: "sin_contactar", l: "Sin contactar", color: "bg-gray-100 text-gray-700" },
    { v: "contactado", l: "Contactado", color: "bg-blue-50 text-blue-700" },
    { v: "respondio", l: "Respondió", color: "bg-amber-50 text-amber-700" },
    { v: "cliente", l: "Cliente", color: "bg-green-50 text-green-700" },
    { v: "descartado", l: "Descartado", color: "bg-red-50 text-red-700" },
];

const EMPTY_FORM = {
    empresa: "", cif: "", email: "", telefono: "",
    contacto_nombre: "", contacto_cargo: "", provincia: "", sector: "",
    tipo_cliente: "empresa",
    estado: "sin_contactar", notas: "",
    proxima_accion_fecha: "", proxima_accion_texto: "",
};

export default function ProspectosTab({ templates }) {
    const [data, setData] = useState({ items: [], counts: {}, pendientes_hoy: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [filterEstado, setFilterEstado] = useState("");
    const [filterTipo, setFilterTipo] = useState(""); // "" | "empresa" | "asesoria"
    const [filterQ, setFilterQ] = useState("");
    const [filterFecha, setFilterFecha] = useState(""); // "" | "hoy" | "semana" | "mes" | "custom"
    const [fechaDesde, setFechaDesde] = useState("");
    const [fechaHasta, setFechaHasta] = useState("");
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkTpl, setBulkTpl] = useState("");
    const [bulkSending, setBulkSending] = useState(false);
    const [bulkJob, setBulkJob] = useState(null); // {job_id, status, total, sent, failed, errors_sample, template_subject}

    async function load() {
        setLoading(true); setError("");
        try {
            const params = new URLSearchParams();
            if (filterEstado) params.set("estado", filterEstado);
            if (filterTipo) params.set("tipo", filterTipo);
            if (filterQ) params.set("q", filterQ);
            // Filtro por fecha de introducción
            const now = new Date();
            const isoDate = (d) => d.toISOString().slice(0, 10);
            if (filterFecha === "hoy") {
                params.set("desde", isoDate(now));
            } else if (filterFecha === "semana") {
                const d = new Date(now); d.setDate(d.getDate() - 7);
                params.set("desde", isoDate(d));
            } else if (filterFecha === "mes") {
                const d = new Date(now); d.setDate(d.getDate() - 30);
                params.set("desde", isoDate(d));
            } else if (filterFecha === "custom") {
                if (fechaDesde) params.set("desde", fechaDesde);
                if (fechaHasta) params.set("hasta", fechaHasta);
            }
            const { data: d } = await api.get(`/admin/prospectos${params.toString() ? "?" + params.toString() : ""}`);
            setData(d);
        } catch (e) {
            setError(formatApiError(e, "Error al cargar prospectos"));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterEstado, filterTipo, filterFecha, fechaDesde, fechaHasta]);

    function openNew() {
        setEditing(null);
        setForm(EMPTY_FORM);
        setShowForm(true);
    }

    function openEdit(p) {
        setEditing(p.id);
        setForm({
            empresa: p.empresa || "", cif: p.cif || "", email: p.email || "", telefono: p.telefono || "",
            contacto_nombre: p.contacto_nombre || "", contacto_cargo: p.contacto_cargo || "",
            provincia: p.provincia || "", sector: p.sector || "",
            tipo_cliente: p.tipo_cliente || "empresa",
            estado: p.estado || "sin_contactar", notas: p.notas || "",
            proxima_accion_fecha: p.proxima_accion_fecha || "", proxima_accion_texto: p.proxima_accion_texto || "",
        });
        setShowForm(true);
    }

    async function save() {
        if (!form.empresa.trim()) { setError("El nombre de la empresa es obligatorio"); return; }
        setSaving(true); setError("");
        try {
            const payload = { ...form };
            // Limpiar strings vacíos a null (excepto empresa y estado)
            Object.keys(payload).forEach(k => {
                if (typeof payload[k] === "string" && payload[k].trim() === "" && k !== "empresa" && k !== "estado") {
                    delete payload[k];
                }
            });
            if (editing) {
                await api.put(`/admin/prospectos/${editing}`, payload);
            } else {
                await api.post("/admin/prospectos", payload);
            }
            setShowForm(false);
            await load();
        } catch (e) {
            setError(formatApiError(e, "Error al guardar"));
        } finally {
            setSaving(false);
        }
    }

    async function remove(p) {
        if (!confirm(`¿Eliminar prospecto "${p.empresa}"?`)) return;
        try {
            await api.delete(`/admin/prospectos/${p.id}`);
            await load();
        } catch (e) {
            setError(formatApiError(e, "Error al eliminar"));
        }
    }

    async function sendTemplate(p, templateId) {
        if (!p.email) { alert("Este prospecto no tiene email"); return; }
        try {
            await api.post(`/admin/prospectos/${p.id}/send-email`, { template_id: templateId });
            alert(`Email enviado a ${p.email}. Estado actualizado a Contactado.`);
            await load();
        } catch (e) {
            alert(formatApiError(e, "Error al enviar"));
        }
    }

    function toggleOne(id) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    function toggleAll(items) {
        setSelectedIds(prev => {
            const allIds = items.filter(p => p.email).map(p => p.id);
            const allSelected = allIds.every(id => prev.has(id));
            if (allSelected) {
                // deseleccionar los visibles
                const next = new Set(prev);
                allIds.forEach(id => next.delete(id));
                return next;
            }
            return new Set([...prev, ...allIds]);
        });
    }

    function clearSelection() {
        setSelectedIds(new Set());
    }

    async function bulkChangeType(newTipo) {
        if (selectedIds.size === 0) { alert("Selecciona al menos un prospecto"); return; }
        const ids = Array.from(selectedIds);
        const tipoLabel = newTipo === "asesoria" ? "Asesoría / Gestoría" : "Empresa";
        if (!confirm(`¿Cambiar el tipo de ${ids.length} ${ids.length === 1 ? "prospecto" : "prospectos"} a "${tipoLabel}"?`)) return;
        try {
            const { data } = await api.post("/admin/prospectos/bulk-update", {
                prospecto_ids: ids,
                tipo_cliente: newTipo,
            });
            alert(`✅ ${data.modified} ${data.modified === 1 ? "prospecto actualizado" : "prospectos actualizados"} a "${tipoLabel}".`);
            clearSelection();
            await load();
        } catch (e) {
            alert(formatApiError(e, "Error al actualizar"));
        }
    }

    async function sendBulkToSelected() {
        if (selectedIds.size === 0) { alert("Selecciona al menos un prospecto"); return; }
        if (!bulkTpl) { alert("Elige una plantilla de email"); return; }
        const ids = Array.from(selectedIds);
        if (!confirm(`¿Enviar el email seleccionado a ${ids.length} ${ids.length === 1 ? "prospecto" : "prospectos"}?\n\nSe marcarán automáticamente como "Contactado" a medida que se envíen (≈4 emails/seg).`)) return;
        setBulkSending(true);
        try {
            const { data } = await api.post("/admin/prospectos/bulk-send", {
                template_id: bulkTpl,
                prospecto_ids: ids,
            });
            if (data.queued && data.job_id) {
                setBulkJob({
                    job_id: data.job_id,
                    total: data.total_objetivo,
                    sent: 0,
                    failed: 0,
                    status: "running",
                    template_subject: data.plantilla,
                    eta_minutos: data.eta_minutos,
                    errors_sample: [],
                });
                clearSelection();
            } else {
                alert(`Campaña: ${data.mensaje || "Sin destinatarios"}\n\nObjetivo: ${data.total_objetivo}\nEnviados: ${data.enviados}\nFallidos: ${data.fallidos}`);
            }
        } catch (e) {
            alert(formatApiError(e, "Error al enviar campaña"));
        } finally {
            setBulkSending(false);
        }
    }

    // Polling del job activo cada 3 seg
    useEffect(() => {
        if (!bulkJob?.job_id || (bulkJob.status !== "running")) return;
        let cancelled = false;
        const t = setInterval(async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/admin/bulk-jobs/${bulkJob.job_id}`);
                if (cancelled) return;
                setBulkJob(prev => prev ? { ...prev, ...data } : data);
                if (data.status !== "running") {
                    clearInterval(t);
                    await load();
                }
            } catch (e) {
                // Si el job desaparece, paramos el polling
                clearInterval(t);
            }
        }, 3000);
        return () => { cancelled = true; clearInterval(t); };
    }, [bulkJob?.job_id, bulkJob?.status]); // eslint-disable-line

    async function importCsv(file) {
        setImporting(true); setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const token = localStorage.getItem("gf_token");
            const res = await fetch(`${API}/admin/prospectos/import`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.detail || "Error al importar");
            alert(`Importación completada:\n· ${json.created} prospectos creados\n· ${json.skipped_duplicates} duplicados ignorados${json.errors?.length ? "\n· Errores: " + json.errors.join(" | ") : ""}`);
            await load();
        } catch (e) {
            setError(e.message || "Error al importar CSV");
        } finally {
            setImporting(false);
        }
    }

    function downloadTemplate() {
        const token = localStorage.getItem("gf_token");
        fetch(`${API}/admin/prospectos/template.csv`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "plantilla_prospectos.csv";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
    }

    function downloadAll() {
        const token = localStorage.getItem("gf_token");
        fetch(`${API}/admin/prospectos/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `prospectos_${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
    }

    const filteredItems = useMemo(() => {
        if (!filterQ) return data.items;
        const q = filterQ.toLowerCase();
        return data.items.filter(p =>
            (p.empresa || "").toLowerCase().includes(q) ||
            (p.contacto_nombre || "").toLowerCase().includes(q) ||
            (p.email || "").toLowerCase().includes(q) ||
            (p.cif || "").toLowerCase().includes(q)
        );
    }, [data.items, filterQ]);

    return (
        <div className="space-y-6 mb-10">
            {/* Header + estadísticas */}
            <div className="gf-card border-t-4 border-t-[var(--gf-gold)]">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                        <div className="micro-label">— CRM de prospectos</div>
                        <h2 className="serif text-2xl mt-1">Cartera de contactos</h2>
                        <p className="text-sm text-[var(--gf-text-secondary)] mt-2 max-w-xl">
                            Aquí guardas los contactos que vas recopilando (Cámaras, eInforma, LinkedIn, asesorías…).
                            Cuando rellenen La Máquina se cruzarán automáticamente con sus leads.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={openNew} className="btn-gold text-sm" data-testid="prospecto-new-btn">
                            <Plus size={14} weight="bold" /> Nuevo
                        </button>
                        <label className="btn-outline text-sm cursor-pointer" data-testid="prospecto-import-btn">
                            <UploadSimple size={14} /> {importing ? "Importando…" : "Importar CSV"}
                            <input type="file" accept=".csv" hidden onChange={(e) => { if (e.target.files[0]) importCsv(e.target.files[0]); e.target.value = ""; }} />
                        </label>
                        <button onClick={downloadTemplate} className="text-xs text-[var(--gf-text-secondary)] underline underline-offset-4 hover:text-[var(--gf-navy)] self-center">
                            Plantilla CSV
                        </button>
                        <button onClick={downloadAll} className="btn-outline text-sm" data-testid="prospecto-export-btn">
                            <FileArrowDown size={14} /> Exportar
                        </button>
                    </div>
                </div>

                {/* Contadores por estado */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
                    <FilterChip label="Todos" count={data.total} active={!filterEstado} onClick={() => setFilterEstado("")} />
                    {ESTADOS.map(e => (
                        <FilterChip key={e.v} label={e.l} count={data.counts?.[e.v] || 0} active={filterEstado === e.v}
                                    onClick={() => setFilterEstado(filterEstado === e.v ? "" : e.v)} colorClass={e.color} />
                    ))}
                </div>

                {/* Filtro por tipo de cliente */}
                <div className="flex flex-wrap gap-2 mb-4" data-testid="filter-tipo">
                    <span className="text-[10px] tracking-widest uppercase text-[var(--gf-text-muted)] self-center mr-2">Tipo:</span>
                    <FilterChip label="Todos" active={!filterTipo} onClick={() => setFilterTipo("")} />
                    <FilterChip label="Empresa" active={filterTipo === "empresa"} onClick={() => setFilterTipo(filterTipo === "empresa" ? "" : "empresa")} colorClass="bg-[var(--gf-navy)]/10 text-[var(--gf-navy)]" />
                    <FilterChip label="Asesoría" active={filterTipo === "asesoria"} onClick={() => setFilterTipo(filterTipo === "asesoria" ? "" : "asesoria")} colorClass="bg-[var(--gf-gold)]/15 text-[var(--gf-gold)]" />
                </div>

                {/* Filtro por fecha de introducción */}
                <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="filter-fecha">
                    <span className="text-[10px] tracking-widest uppercase text-[var(--gf-text-muted)] self-center mr-2">Importados:</span>
                    <FilterChip label="Todos" active={!filterFecha} onClick={() => { setFilterFecha(""); setFechaDesde(""); setFechaHasta(""); }} />
                    <FilterChip label="Hoy" active={filterFecha === "hoy"} onClick={() => setFilterFecha(filterFecha === "hoy" ? "" : "hoy")} />
                    <FilterChip label="Últimos 7 días" active={filterFecha === "semana"} onClick={() => setFilterFecha(filterFecha === "semana" ? "" : "semana")} />
                    <FilterChip label="Últimos 30 días" active={filterFecha === "mes"} onClick={() => setFilterFecha(filterFecha === "mes" ? "" : "mes")} />
                    <FilterChip label="Personalizado" active={filterFecha === "custom"} onClick={() => setFilterFecha(filterFecha === "custom" ? "" : "custom")} />
                    {filterFecha === "custom" && (
                        <div className="flex items-center gap-2 ml-2">
                            <input
                                type="date"
                                value={fechaDesde}
                                onChange={(e) => setFechaDesde(e.target.value)}
                                className="gf-input text-xs py-1"
                                title="Desde"
                                data-testid="filter-fecha-desde"
                            />
                            <span className="text-xs text-[var(--gf-text-muted)]">→</span>
                            <input
                                type="date"
                                value={fechaHasta}
                                onChange={(e) => setFechaHasta(e.target.value)}
                                className="gf-input text-xs py-1"
                                title="Hasta"
                                data-testid="filter-fecha-hasta"
                            />
                        </div>
                    )}
                </div>

                {/* Búsqueda */}
                <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gf-text-muted)]" />
                    <input type="text" placeholder="Buscar por empresa, contacto, email, CIF…"
                           value={filterQ} onChange={(e) => setFilterQ(e.target.value)}
                           className="gf-input pl-9 text-sm" data-testid="prospecto-search" />
                </div>

                {/* Pendientes hoy */}
                {data.pendientes_hoy > 0 && (
                    <div className="mt-4 border-l-2 border-[var(--gf-warning)] bg-amber-50 pl-4 py-2 text-sm" data-testid="prospecto-pendientes">
                        <Warning size={14} weight="fill" className="inline text-[var(--gf-warning)] mr-2" />
                        <strong>{data.pendientes_hoy}</strong> {data.pendientes_hoy === 1 ? "acción pendiente" : "acciones pendientes"} para hoy o anteriores.
                    </div>
                )}
            </div>

            {error && (
                <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50">
                    {error}
                </div>
            )}

            {/* Widget de progreso de campaña masiva */}
            {bulkJob && (
                <BulkJobProgress job={bulkJob} onClose={() => setBulkJob(null)} />
            )}

            {/* Barra de envío masivo (solo cuando hay selección) */}
            {selectedIds.size > 0 && (
                <div className="gf-card border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-cream)] sticky top-4 z-30 shadow-sm" data-testid="bulk-send-bar">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4 justify-between">
                        <div className="text-sm">
                            <strong className="text-[var(--gf-navy)]">{selectedIds.size}</strong> {selectedIds.size === 1 ? "prospecto seleccionado" : "prospectos seleccionados"} ·
                            <button onClick={clearSelection} className="ml-2 text-xs underline underline-offset-4 text-[var(--gf-text-secondary)] hover:text-[var(--gf-danger)]" data-testid="bulk-clear-selection">
                                Limpiar selección
                            </button>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
                            <div className="flex items-center gap-1 border border-[var(--gf-border)] bg-white px-2 py-1">
                                <span className="text-[10px] tracking-widest uppercase text-[var(--gf-text-muted)] mr-1">Marcar como:</span>
                                <button
                                    onClick={() => bulkChangeType("asesoria")}
                                    className="text-xs px-2 py-1 hover:bg-[var(--gf-gold)]/15 text-[var(--gf-gold)] font-semibold"
                                    data-testid="bulk-set-asesoria"
                                    title="Cambiar tipo de los seleccionados a Asesoría / Gestoría"
                                >
                                    Asesoría
                                </button>
                                <span className="text-[var(--gf-text-muted)]">·</span>
                                <button
                                    onClick={() => bulkChangeType("empresa")}
                                    className="text-xs px-2 py-1 hover:bg-[var(--gf-navy)]/10 text-[var(--gf-navy)] font-semibold"
                                    data-testid="bulk-set-empresa"
                                    title="Cambiar tipo de los seleccionados a Empresa"
                                >
                                    Empresa
                                </button>
                            </div>
                            <select
                                value={bulkTpl}
                                onChange={(e) => setBulkTpl(e.target.value)}
                                className="gf-input text-sm min-w-[260px]"
                                data-testid="bulk-template-select"
                            >
                                <option value="">— Elige plantilla de email —</option>
                                {(templates || []).filter(t => t.active).map(t => (
                                    <option key={t.id} value={t.id}>{t.subject}</option>
                                ))}
                            </select>
                            <button
                                onClick={sendBulkToSelected}
                                disabled={bulkSending || !bulkTpl}
                                className="btn-gold text-sm whitespace-nowrap"
                                data-testid="bulk-send-selected"
                            >
                                <PaperPlaneTilt size={14} weight="fill" />
                                {bulkSending ? "Enviando…" : `Enviar a ${selectedIds.size}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabla */}
            <div className="gf-card">
                {loading ? (
                    <div className="text-center py-10 text-[var(--gf-text-secondary)] text-sm italic">Cargando…</div>
                ) : filteredItems.length === 0 ? (
                    <div className="text-center py-10 text-[var(--gf-text-secondary)] text-sm italic">
                        {data.total === 0 ? "Aún no hay prospectos. Importa un CSV o crea uno manualmente para empezar." : "Sin resultados con estos filtros."}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="data-table" data-testid="prospectos-table">
                            <thead>
                                <tr>
                                    <th className="w-8">
                                        <input
                                            type="checkbox"
                                            checked={filteredItems.length > 0 && filteredItems.filter(p => p.email).every(p => selectedIds.has(p.id))}
                                            onChange={() => toggleAll(filteredItems)}
                                            title="Seleccionar todos los visibles con email"
                                            data-testid="select-all-checkbox"
                                        />
                                    </th>
                                    <th>Empresa</th>
                                    <th>Tipo</th>
                                    <th>Contacto</th>
                                    <th>Email / Tel</th>
                                    <th>Ubicación</th>
                                    <th>Estado</th>
                                    <th>Próxima acción</th>
                                    <th>Cruce</th>
                                    <th className="text-right col-sticky-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((p) => (
                                    <tr key={p.id} className={selectedIds.has(p.id) ? "bg-[var(--gf-cream)]" : ""}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(p.id)}
                                                disabled={!p.email}
                                                onChange={() => toggleOne(p.id)}
                                                title={p.email ? "Seleccionar" : "Sin email — no se puede enviar"}
                                                data-testid={`select-${p.id}`}
                                            />
                                        </td>
                                        <td className="text-sm">
                                            <div className="font-semibold text-[var(--gf-navy)]">{p.empresa}</div>
                                            {p.cif && <div className="text-[10px] mono text-[var(--gf-text-muted)]">{p.cif}</div>}
                                            {p.sector && <div className="text-[10px] text-[var(--gf-text-muted)]">{p.sector}</div>}
                                        </td>
                                        <td>
                                            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 font-semibold ${p.tipo_cliente === "asesoria" ? "bg-[var(--gf-gold)]/15 text-[var(--gf-gold)]" : "bg-[var(--gf-navy)]/10 text-[var(--gf-navy)]"}`}>
                                                {p.tipo_cliente === "asesoria" ? "Asesoría" : "Empresa"}
                                            </span>
                                        </td>
                                        <td className="text-sm">
                                            {p.contacto_nombre || "—"}
                                            {p.contacto_cargo && <div className="text-[10px] text-[var(--gf-text-muted)]">{p.contacto_cargo}</div>}
                                        </td>
                                        <td className="text-xs">
                                            {p.email && <div><a href={`mailto:${p.email}`} className="hover:text-[var(--gf-gold)] underline-offset-4 hover:underline">{p.email}</a></div>}
                                            {p.telefono && <div className="text-[var(--gf-text-muted)] mono">{p.telefono}</div>}
                                        </td>
                                        <td className="text-xs text-[var(--gf-text-secondary)]">{p.provincia || "—"}</td>
                                        <td>
                                            <EstadoBadge estado={p.estado} />
                                            {p.ultima_accion && <div className="text-[9px] text-[var(--gf-text-muted)] mt-0.5">{p.ultima_accion.slice(0, 60)}</div>}
                                        </td>
                                        <td className="text-xs">
                                            {p.proxima_accion_fecha ? (
                                                <div className={p.accion_pendiente ? "text-[var(--gf-warning)] font-semibold" : ""}>
                                                    <div className="mono">{p.proxima_accion_fecha}</div>
                                                    {p.proxima_accion_texto && <div className="text-[10px] text-[var(--gf-text-muted)]">{p.proxima_accion_texto}</div>}
                                                </div>
                                            ) : <span className="text-[var(--gf-text-muted)]">—</span>}
                                        </td>
                                        <td className="text-[10px]">
                                            {p.has_lead && <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 mr-1">Lead</span>}
                                            {p.is_user && <span className="bg-green-100 text-green-800 px-1.5 py-0.5">User</span>}
                                            {!p.has_lead && !p.is_user && <span className="text-[var(--gf-text-muted)]">—</span>}
                                        </td>
                                        <td className="text-right col-sticky-right whitespace-nowrap" style={{ minWidth: 90 }}>
                                            <div className="inline-flex items-center gap-1">
                                                <button onClick={() => openEdit(p)} className="p-1.5 text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)]" title="Editar" data-testid={`prospecto-edit-${p.id}`}>
                                                    <PencilSimple size={16} />
                                                </button>
                                                <button onClick={() => remove(p)} className="p-1.5 text-[var(--gf-danger)] hover:opacity-70" title="Eliminar" data-testid={`prospecto-delete-${p.id}`}>
                                                    <Trash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de creación/edición */}
            {showForm && (
                <ProspectoForm
                    form={form} setForm={setForm}
                    editing={editing} saving={saving}
                    onSave={save} onClose={() => setShowForm(false)}
                />
            )}
        </div>
    );
}

function FilterChip({ label, count, active, onClick, colorClass }) {
    return (
        <button onClick={onClick}
                className={`text-left px-3 py-2 border transition ${active ? "border-[var(--gf-gold)] bg-[var(--gf-cream)]" : "border-[var(--gf-border)] bg-white hover:border-[var(--gf-text-muted)]"}`}
                data-testid={`prospecto-filter-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-[10px] tracking-widest uppercase text-[var(--gf-text-secondary)]">{label}</div>
            <div className="mono font-semibold text-lg mt-0.5 text-[var(--gf-navy)]">{count}</div>
        </button>
    );
}

function EstadoBadge({ estado }) {
    const e = ESTADOS.find(x => x.v === estado) || ESTADOS[0];
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 font-semibold ${e.color}`}>{e.l}</span>;
}

function ProspectoForm({ form, setForm, editing, saving, onSave, onClose }) {
    function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="prospecto-modal">
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <div className="micro-label">— {editing ? "Editar prospecto" : "Nuevo prospecto"}</div>
                        <h2 className="serif text-2xl mt-1">{editing ? "Modificar datos" : "Añadir contacto a tu cartera"}</h2>
                    </div>
                    <button onClick={onClose} className="text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)]" data-testid="prospecto-modal-close">
                        <X size={20} />
                    </button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <FormField label="Empresa *" value={form.empresa} onChange={(v) => set("empresa", v)} testId="pf-empresa" />
                    <FormField label="CIF" value={form.cif} onChange={(v) => set("cif", v)} testId="pf-cif" />
                    <FormField label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testId="pf-email" />
                    <FormField label="Teléfono" value={form.telefono} onChange={(v) => set("telefono", v)} testId="pf-telefono" />
                    <FormField label="Persona de contacto" value={form.contacto_nombre} onChange={(v) => set("contacto_nombre", v)} testId="pf-contacto" />
                    <FormField label="Cargo" value={form.contacto_cargo} onChange={(v) => set("contacto_cargo", v)} testId="pf-cargo" />
                    <FormField label="Provincia" value={form.provincia} onChange={(v) => set("provincia", v)} testId="pf-provincia" />
                    <FormField label="Sector / actividad" value={form.sector} onChange={(v) => set("sector", v)} testId="pf-sector" />
                </div>

                <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">Tipo de cliente</label>
                        <select value={form.tipo_cliente} onChange={(e) => set("tipo_cliente", e.target.value)} className="gf-input text-sm" data-testid="pf-tipo">
                            <option value="empresa">Empresa</option>
                            <option value="asesoria">Asesoría / Gestoría</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">Estado</label>
                        <select value={form.estado} onChange={(e) => set("estado", e.target.value)} className="gf-input text-sm" data-testid="pf-estado">
                            {ESTADOS.map(e => <option key={e.v} value={e.v}>{e.l}</option>)}
                        </select>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <FormField label="Próxima acción · fecha" type="date" value={form.proxima_accion_fecha} onChange={(v) => set("proxima_accion_fecha", v)} testId="pf-fecha" />
                    <FormField label="Próxima acción · descripción" value={form.proxima_accion_texto} onChange={(v) => set("proxima_accion_texto", v)} testId="pf-accion" />
                </div>

                <div className="mt-4">
                    <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">Notas</label>
                    <textarea value={form.notas} onChange={(e) => set("notas", e.target.value)} rows={3} className="gf-input text-sm" placeholder="Origen, intenciones, históricos de conversación…" data-testid="pf-notas" />
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[var(--gf-border)]">
                    <button onClick={onClose} className="btn-outline text-sm">Cancelar</button>
                    <button onClick={onSave} disabled={saving} className="btn-gold text-sm" data-testid="prospecto-save">
                        {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear prospecto"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FormField({ label, value, onChange, type = "text", testId }) {
    return (
        <div>
            <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">{label}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="gf-input text-sm" data-testid={testId} />
        </div>
    );
}


function BulkJobProgress({ job, onClose }) {
    const total = job.total || 0;
    const sent = job.sent || 0;
    const failed = job.failed || 0;
    const done = sent + failed;
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    const running = job.status === "running";
    const crashed = job.status === "crashed";
    const finished = job.status === "done";
    const allFailed = finished && sent === 0 && failed > 0;
    const someFailed = finished && failed > 0 && sent > 0;

    let badgeColor = "bg-blue-50 border-blue-200 text-blue-900";
    let title = "Campaña en marcha…";
    if (crashed) { badgeColor = "bg-red-50 border-red-300 text-red-900"; title = "❌ Campaña interrumpida"; }
    else if (allFailed) { badgeColor = "bg-red-50 border-red-300 text-red-900"; title = "❌ Todos los emails fallaron"; }
    else if (someFailed) { badgeColor = "bg-amber-50 border-amber-300 text-amber-900"; title = "⚠️ Campaña finalizada con errores"; }
    else if (finished) { badgeColor = "bg-green-50 border-green-300 text-green-900"; title = "✅ Campaña completada"; }

    return (
        <div className={`gf-card border-l-4 ${badgeColor} sticky top-4 z-30 shadow-sm`} data-testid="bulk-job-progress">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center justify-between mb-1">
                        <strong className="text-sm">{title}</strong>
                        <button onClick={onClose} className="text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)] text-xs underline underline-offset-2" data-testid="bulk-job-close">
                            Cerrar
                        </button>
                    </div>
                    <div className="text-xs text-[var(--gf-text-secondary)] mb-2">
                        <span className="mono">{done}/{total}</span> procesados ·
                        <span className="text-green-700 mono mx-1">✓ {sent}</span> enviados ·
                        <span className="text-red-700 mono mx-1">✗ {failed}</span> fallidos
                        {running && job.eta_minutos && <span className="ml-2 text-[var(--gf-text-muted)]">(ETA ≈ {job.eta_minutos} min)</span>}
                    </div>
                    {/* Barra de progreso */}
                    <div className="h-2 bg-white border border-[var(--gf-border)] overflow-hidden">
                        <div
                            className={`h-full transition-all duration-500 ${allFailed || crashed ? "bg-red-500" : someFailed ? "bg-amber-500" : "bg-[var(--gf-gold)]"}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    {/* Plantilla */}
                    <div className="text-[10px] text-[var(--gf-text-muted)] mt-2 italic truncate">
                        Plantilla: {job.template_subject}
                    </div>
                    {/* Errores (si hay) */}
                    {job.errors_sample && job.errors_sample.length > 0 && (
                        <details className="mt-3" data-testid="bulk-job-errors">
                            <summary className="text-xs cursor-pointer text-red-700 font-semibold">
                                Ver {failed === 1 ? "el error" : `errores (últimos ${job.errors_sample.length})`}
                            </summary>
                            <div className="mt-2 text-[10px] mono bg-white p-2 border border-red-200 max-h-32 overflow-y-auto">
                                {job.errors_sample.map((err, i) => (
                                    <div key={i} className="py-0.5 border-b border-red-100 last:border-b-0 break-words">
                                        {err}
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                    {crashed && job.crash_error && (
                        <div className="mt-2 text-[10px] mono bg-white p-2 border border-red-200 text-red-900 break-words">
                            {job.crash_error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
